import { useState } from "react";
import { toast } from "sonner";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { useFetch } from "../../lib/useFetch";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { AdminH } from "./AdminLayout";

const num = (v) => {
  const n = Number(String(v).trim().replace(",", "."));
  if (String(v).trim() === "" || Number.isNaN(n)) throw new Error("Enter the percentage as a number, e.g. 7 or 5,5");
  return n;
};

const Row = ({ t, reload }) => {
  const [e, setE] = useState(null);
  const save = async () => {
    try {
      await api.put(`/admin/tax-values/${t.id}`, { name: e.name, rate: num(e.rate) });
      setE(null);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${t.name} – ${t.rate}%?`)) return;
    try {
      await api.delete(`/admin/tax-values/${t.id}`);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const lock = t.used ? "Used by a company, an event or a charter: it can't be changed or deleted" : undefined;
  return (
    <tr className="border-t" data-testid={`tax-value-row-${t.rate}`}>
      {e ? (<>
        <td className="py-2 pr-2"><Input value={e.name} onChange={(x) => setE({ ...e, name: x.target.value })} data-testid={`tax-value-edit-name-${t.rate}`} /></td>
        <td className="py-2 pr-2"><Input value={e.rate} onChange={(x) => setE({ ...e, rate: x.target.value })} className="w-24" data-testid={`tax-value-edit-rate-${t.rate}`} /></td>
        <td className="py-2 text-right"><button onClick={save} className="mr-2 text-[#00A8B5]" data-testid={`tax-value-save-${t.rate}`}><Check size={16} /></button><button onClick={() => setE(null)} className="text-slate-400"><X size={16} /></button></td>
      </>) : (<>
        <td className="py-2 font-semibold text-[#0A2540]">{t.name}</td>
        <td className="py-2">{t.rate}%</td>
        <td className="py-2 text-right">
          {t.used && <span className="mr-3 text-xs text-slate-500" data-testid={`tax-value-used-${t.rate}`}>In use</span>}
          <button disabled={t.used} title={lock} onClick={() => setE({ name: t.name, rate: String(t.rate) })} className="mr-2 text-slate-400 hover:text-[#0A2540] disabled:opacity-30" data-testid={`tax-value-edit-${t.rate}`}><Pencil size={16} /></button>
          <button disabled={t.used} title={lock} onClick={del} className="text-slate-400 hover:text-rose-500 disabled:opacity-30" data-testid={`tax-value-delete-${t.rate}`}><Trash2 size={16} /></button>
        </td>
      </>)}
    </tr>
  );
};

export default function AdminTaxValues() {
  const { data, reload } = useFetch("/admin/tax-values");
  const [n, setN] = useState({ name: "", rate: "" });
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/tax-values", { name: n.name, rate: num(n.rate) });
      setN({ name: "", rate: "" });
      toast.success("Tax value added");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="space-y-6" data-testid="admin-tax-values">
      <AdminH title="Tax values" sub="Tax rates offered in the tax menus of companies, events, charters and manual proforma/invoice lines. A value can be changed or deleted only while it is not used." />
      <div className="max-w-2xl rounded-2xl border border-slate-200 bg-white p-6">
        <table className="w-full table-fixed text-sm">
          <thead><tr className="text-left text-xs uppercase text-slate-500"><th className="w-1/2 pb-2">Name</th><th className="w-1/4 pb-2">Value %</th><th className="w-1/4" /></tr></thead>
          <tbody>{(data || []).map((t) => <Row key={t.id} t={t} reload={reload} />)}</tbody>
        </table>
        <form onSubmit={add} className="mt-5 flex flex-wrap items-end gap-2 border-t pt-5">
          <Input placeholder="Name, e.g. IVA Portugal" value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} className="w-60" data-testid="tax-value-name" />
          <Input placeholder="%" value={n.rate} onChange={(e) => setN({ ...n, rate: e.target.value })} className="w-24" data-testid="tax-value-rate" />
          <button disabled={!n.name.trim() || !String(n.rate).trim()} className="inline-flex items-center gap-1 rounded-full bg-[#0A2540] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40" data-testid="tax-value-add"><Plus size={14} />Add tax value</button>
        </form>
      </div>
    </div>
  );
}
